import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { api } from "../api/client";
import type {
  Analysis,
  CompareSeries,
  IndicatorsResponse,
  NewsItem,
  Recommendation,
  ResolvedSymbol,
} from "../api/types";
import AnalysisCard from "../components/AnalysisCard";
import CompareDrawer from "../components/CompareDrawer";
import Footer from "../components/Footer";
import FundamentalsCard from "../components/FundamentalsCard";
import IndicatorsPanel from "../components/IndicatorsPanel";
import InsiderActivityCard from "../components/InsiderActivityCard";
import MoveExplanationCard from "../components/MoveExplanationCard";
import NewsList from "../components/NewsList";
import PriceChart from "../components/PriceChart";
import ScoreCard from "../components/ScoreCard";
import ScoreHistory from "../components/ScoreHistory";
import SymbolWarmupChip from "../components/SymbolWarmupChip";

type Horizon = "intraday" | "short" | "medium" | "long";

// While the backend warmup job runs (intraday bars + news + LLM analysis),
// poll quickly so the UI fills in within seconds of the job completing.
const WARMUP_REFETCH_MS = 5_000;
const WARMUP_WINDOW_MS = 5 * 60_000;

export default function SymbolDetail() {
  const { yahoo } = useParams<{ yahoo: string }>();
  const symbol = decodeURIComponent(yahoo || "");
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Compare drawer: A is implicit (the page's symbol), user picks B.
  // ?compare=B in the URL pre-opens the drawer (back-compat for old
  // /compare?a=A&b=B links, redirected here).
  const initialB = searchParams.get("compare");
  const [compareOpen, setCompareOpen] = useState<boolean>(!!initialB);
  const [compareB, setCompareB] = useState<string | null>(initialB);

  const compareQuery = useQuery<CompareSeries[]>({
    queryKey: ["compare", symbol, compareB],
    queryFn: async () =>
      (await api.get<CompareSeries[]>("/compare", { params: { a: symbol, b: compareB, days: 90 } })).data,
    enabled: !!symbol && !!compareB,
  });

  function pickCompare(b: string) {
    setCompareB(b);
    const next = new URLSearchParams(searchParams);
    next.set("compare", b);
    setSearchParams(next, { replace: true });
  }

  function closeCompare() {
    setCompareOpen(false);
    setCompareB(null);
    const next = new URLSearchParams(searchParams);
    next.delete("compare");
    setSearchParams(next, { replace: true });
  }

  // Tracks an auto-resolve attempt for unmapped tickers reached via
  // /s/{ticker_text}. The result yields the resolved yahoo_symbol that
  // the rest of the page actually queries against.
  const [resolveStatus, setResolveStatus] = useState<"idle" | "resolving" | "resolved" | "missing">("idle");
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [resolvedAt, setResolvedAt] = useState<number | null>(null);
  const triedResolve = useRef(false);

  const ind = useQuery<IndicatorsResponse>({
    queryKey: ["indicators", symbol],
    queryFn: async () => (await api.get<IndicatorsResponse>(`/symbols/${encodeURIComponent(symbol)}/indicators`)).data,
    enabled: !!symbol,
    retry: false,
  });

  const indErr = ind.error as AxiosError | undefined;
  const symbolIs404 = indErr?.response?.status === 404;
  const inWarmup = resolvedAt !== null && Date.now() - resolvedAt < WARMUP_WINDOW_MS;

  // First time we see a 404 for this symbol, try resolving it. If yfinance
  // confirms the ticker, the backend inserts the row and queues a warmup
  // job — re-enable the indicator query and start polling for analysis.
  useEffect(() => {
    if (!symbol || triedResolve.current) return;
    if (!symbolIs404) return;
    triedResolve.current = true;
    setResolveStatus("resolving");
    setResolveError(null);
    (async () => {
      try {
        const r = (await api.post<ResolvedSymbol>("/symbols/resolve", { ticker_text: symbol })).data;
        setResolveStatus("resolved");
        setResolvedAt(Date.now());
        qc.invalidateQueries({ queryKey: ["indicators", symbol] });
        qc.invalidateQueries({ queryKey: ["analysis", symbol] });
        qc.invalidateQueries({ queryKey: ["recs-latest", symbol] });
        // If the user actually typed a name like "AAPL" but yfinance returned
        // a different yahoo_symbol (rare; e.g. unicode mapping), surface it.
        if (r.yahoo_symbol && r.yahoo_symbol !== symbol) {
          window.history.replaceState(null, "", `/s/${encodeURIComponent(r.yahoo_symbol)}`);
        }
      } catch (err) {
        const e = err as AxiosError<{ detail?: string }>;
        const msg = e.response?.status === 404
          ? `yfinance has no data for "${symbol}"`
          : e.response?.data?.detail || "Resolve failed";
        setResolveStatus("missing");
        setResolveError(msg);
      }
    })();
  }, [symbol, symbolIs404, qc]);

  const analysis = useQuery<Analysis | null>({
    queryKey: ["analysis", symbol],
    queryFn: async () => (await api.get<Analysis | null>(`/analyses/${encodeURIComponent(symbol)}/latest`)).data,
    enabled: !!symbol && !symbolIs404,
    refetchInterval: inWarmup ? WARMUP_REFETCH_MS : 30_000,
  });

  const news = useQuery<NewsItem[]>({
    queryKey: ["news", symbol],
    queryFn: async () => (await api.get<NewsItem[]>(`/news/${encodeURIComponent(symbol)}`, { params: { limit: 20 } })).data,
    enabled: !!symbol && !symbolIs404,
  });

  const [horizon, setHorizon] = useState<Horizon>("medium");

  const recsLatest = useQuery<Record<string, Recommendation>>({
    queryKey: ["recs-latest", symbol],
    queryFn: async () =>
      (await api.get<Record<string, Recommendation>>(`/recommendations/${encodeURIComponent(symbol)}/latest`)).data,
    enabled: !!symbol && !symbolIs404,
    refetchInterval: inWarmup ? WARMUP_REFETCH_MS : 60_000,
  });

  const recsHistory = useQuery<Recommendation[]>({
    queryKey: ["recs-history", symbol, horizon],
    queryFn: async () =>
      (await api.get<Recommendation[]>(`/recommendations/${encodeURIComponent(symbol)}/history`, {
        params: { horizon, limit: 200 },
      })).data,
    enabled: !!symbol && !symbolIs404,
    refetchInterval: 60_000,
  });

  const [refreshing, setRefreshing] = useState(false);
  const [cooldownMessage, setCooldownMessage] = useState<string | null>(null);

  const refresh = useMutation({
    mutationFn: async () => api.post(`/analyses/${encodeURIComponent(symbol)}/refresh`),
    onMutate: () => {
      setRefreshing(true);
      setCooldownMessage(null);
    },
    onError: (err: AxiosError<{ detail?: { message?: string } }>) => {
      // 429 = daily Re-analyze cap hit. Disable the button and surface the
      // server's message (which includes when the bucket resets) until
      // the user reloads or the next UTC midnight.
      if (err.response?.status === 429) {
        const msg =
          err.response.data?.detail?.message ??
          "Daily Re-analyze limit reached.";
        setCooldownMessage(msg);
        setRefreshing(false);
      }
    },
    onSettled: (_data, error) => {
      // Don't run the 30s backoff on error — the analysis didn't actually
      // queue, no point waiting before re-enabling the button.
      if (error) return;
      setTimeout(() => {
        setRefreshing(false);
        qc.invalidateQueries({ queryKey: ["analysis", symbol] });
      }, 30_000);
    },
  });

  if (!symbol) return <div className="p-6 text-slate-400">Missing symbol.</div>;

  // Hard 404 (yfinance has no data) — show a clean missing-state.
  if (resolveStatus === "missing") {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        <div className="mb-3">
          <Link to="/" className="group inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200">
            <svg viewBox="0 0 12 12" className="h-3 w-3 transition-transform group-hover:-translate-x-0.5" aria-hidden="true">
              <path d="M7.5 2.5L4 6l3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
            Back to watchlist
          </Link>
        </div>
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-6 text-sm text-rose-200">
          <div className="font-mono text-lg text-rose-100">{symbol}</div>
          <p className="mt-2">{resolveError}</p>
        </div>
      </div>
    );
  }

  // First load is still resolving — show the chip; nothing else has data yet.
  const stillResolving = resolveStatus === "resolving" || (symbolIs404 && resolveStatus === "idle");

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <div className="mb-3">
        <Link to="/" className="group inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200">
          <svg viewBox="0 0 12 12" className="h-3 w-3 transition-transform group-hover:-translate-x-0.5" aria-hidden="true">
            <path d="M7.5 2.5L4 6l3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
          Back to watchlist
        </Link>
      </div>

      <header className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0">
          <h1 className="font-mono text-2xl font-semibold tracking-tight text-slate-100">
            {ind.data?.ticker ?? symbol}
          </h1>
          <p className="truncate text-xs text-slate-400">
            {ind.data?.name ?? (stillResolving ? "Looking up…" : "Loading…")}
            {ind.data && <> · {ind.data.region} · {ind.data.currency}</>}
          </p>
        </div>
        <div className="flex items-baseline gap-3">
          <button
            onClick={() => setCompareOpen((v) => !v)}
            aria-pressed={compareOpen}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition ${
              compareOpen || compareB
                ? "border-sky-500/60 bg-sky-500/10 text-sky-300 ring-1 ring-sky-500/20"
                : "border-slate-800 bg-slate-900 text-slate-300 hover:border-sky-500/40 hover:text-sky-300"
            }`}
          >
            <svg viewBox="0 0 16 16" className="h-3 w-3 opacity-80" aria-hidden="true">
              <path d="M3 5h8l-2-2M13 11H5l2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
            Compare
          </button>
          {ind.data?.indicators?.last_close !== null && ind.data?.indicators?.last_close !== undefined && (
            <div className="text-right">
              <div className="text-xl tabular-nums text-slate-100">
                {ind.data.indicators.last_close.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className={`text-xs tabular-nums ${
                (ind.data.indicators.ret_1d ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400"
              }`}>
                {ind.data.indicators.ret_1d === null
                  ? "—"
                  : `${ind.data.indicators.ret_1d >= 0 ? "+" : ""}${(ind.data.indicators.ret_1d * 100).toFixed(2)}%  (1d)`}
              </div>
            </div>
          )}
        </div>
      </header>

      <CompareDrawer
        open={compareOpen}
        yahooA={symbol}
        valueB={compareB}
        onPick={pickCompare}
        onClose={closeCompare}
        compare={compareQuery.data}
      />

      {(stillResolving || (inWarmup && !analysis.data)) && (
        <div className="mb-4">
          <SymbolWarmupChip kind={stillResolving ? "resolving" : "warming"} />
        </div>
      )}

      {!stillResolving && (
        <div
          className={`space-y-4 transition-[border-color,padding] duration-200 ${
            compareB && compareQuery.data
              ? "border-l border-sky-500/30 pl-4"
              : "border-l border-transparent pl-0"
          }`}
        >
          <PriceChart yahooSymbol={symbol} compare={compareB ? compareQuery.data : undefined} />
          <MoveExplanationCard yahoo={symbol} />
          <ScoreCard recs={recsLatest.data ?? {}} />
          <FundamentalsCard yahoo={symbol} lastClose={ind.data?.indicators?.last_close ?? null} />
          <ScoreHistory
            history={recsHistory.data ?? []}
            loading={recsHistory.isLoading}
            horizon={horizon}
            onHorizon={setHorizon}
          />
          <AnalysisCard
            analysis={analysis.data ?? null}
            onRefresh={() => refresh.mutate()}
            refreshing={refreshing}
            cooldownMessage={cooldownMessage}
          />
          {ind.data && <IndicatorsPanel ind={ind.data.indicators} />}
          <InsiderActivityCard yahoo={symbol} />
          <NewsList items={news.data ?? []} />
        </div>
      )}

      <Footer />
    </div>
  );
}
